import React, { useCallback, useEffect, useMemo, useState } from "react";
import { addGuest, getGuests, removeGuest, resendGuestInvite, updateGuest } from "../api/rsvp";
import GuestCsvUpload from "./GuestCsvUpload";
import {
  GuestTicketTier,
  GuestWithRsvp,
  GUEST_TIER_OPTIONS,
  TICKET_TIER_DESCRIPTIONS,
  TICKET_TIER_LABELS,
} from "../types";

interface Props {
  weddingId: string;
}

const GuestListManager: React.FC<Props> = ({ weddingId }) => {
  const [guests, setGuests] = useState<GuestWithRsvp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [notice, setNotice] = useState("");
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTier, setEditTier] = useState<GuestTicketTier>("party_not_presents");
  const [editPlusOnes, setEditPlusOnes] = useState(0);
  const [savingEdit, setSavingEdit] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    ticket_tier: "party_not_presents" as GuestTicketTier,
    plus_one_limit: 0,
  });

  const loadGuests = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await getGuests(weddingId);
      setGuests(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load guests");
    } finally {
      setLoading(false);
    }
  }, [weddingId]);

  useEffect(() => {
    loadGuests();
  }, [loadGuests]);

  const guestById = useMemo(
    () => Object.fromEntries(guests.map((guest) => [guest.id, guest])),
    [guests]
  );

  const guestOfLabel = (guest: GuestWithRsvp) => {
    if (!guest.is_plus_one && !guest.parent_guest_id) return "N/A";
    const primary =
      (guest.parent_guest_id && guestById[guest.parent_guest_id]?.full_name) ||
      guest.invited_by;
    if (guest.is_plus_one) {
      return primary ? `Plus-one of ${primary}` : "Plus-one";
    }
    return primary || "N/A";
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdding(true);
    setError("");
    setWarning("");
    setNotice("");
    try {
      const { data } = await addGuest({
        wedding_id: weddingId,
        full_name: form.full_name,
        email: form.email || undefined,
        ticket_tier: form.ticket_tier,
        plus_one_limit:
          form.ticket_tier === "inner_circle_plus_more" ? form.plus_one_limit : 0,
      });
      setForm({
        full_name: "",
        email: "",
        ticket_tier: "party_not_presents",
        plus_one_limit: 0,
      });
      if (data.email_warning) {
        setWarning(data.email_warning);
      }
      await loadGuests();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add guest");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (guestId: string) => {
    if (!window.confirm("Remove this guest from the invite list?")) return;
    setError("");
    try {
      await removeGuest(guestId);
      await loadGuests();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to remove guest");
    }
  };

  const startEdit = (guest: GuestWithRsvp) => {
    setEditingId(guest.id);
    setEditTier((guest.ticket_tier || "party_not_presents") as GuestTicketTier);
    setEditPlusOnes(guest.plus_one_limit || 0);
  };

  const saveEdit = async (guestId: string) => {
    setSavingEdit(true);
    setError("");
    try {
      await updateGuest(guestId, {
        ticket_tier: editTier,
        plus_one_limit: editTier === "inner_circle_plus_more" ? editPlusOnes : 0,
      });
      setEditingId(null);
      await loadGuests();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update guest");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleResendInvite = async (guestId: string) => {
    setError("");
    setWarning("");
    setNotice("");
    setResendingId(guestId);
    try {
      await resendGuestInvite(guestId);
      setNotice("Invitation email sent.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to resend invitation");
    } finally {
      setResendingId(null);
    }
  };

  const rsvpStatusLabel = (guest: GuestWithRsvp) => {
    const status = guest.rsvp?.status;
    if (!status || status === "pending") return "Not Yet RSVP'd";
    if (status === "confirmed") return "Confirmed";
    return "Declined";
  };

  return (
    <div className="guest-manager">
      {error && <div className="error-message inline">{error}</div>}
      {warning && <div className="error-message inline">{warning}</div>}
      {notice && <div className="success-message inline">{notice}</div>}

      {loading ? (
        <p className="form-hint">Loading guests...</p>
      ) : guests.length === 0 ? (
        <p className="form-hint">No guests on the invite list yet.</p>
      ) : (
        <div className="guest-table-wrap">
          <table className="guest-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Guest Of</th>
                <th>Tier</th>
                <th>RSVP</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {guests.map((guest) => (
                <tr key={guest.id}>
                  <td>{guest.full_name}</td>
                  <td>{guest.email || "—"}</td>
                  <td>{guestOfLabel(guest)}</td>
                  <td>
                    {editingId === guest.id && !guest.is_plus_one ? (
                      <div className="inline-edit">
                        <select
                          value={editTier}
                          onChange={(e) =>
                            setEditTier(e.target.value as GuestTicketTier)
                          }
                        >
                          {GUEST_TIER_OPTIONS.map((tier) => (
                            <option key={tier} value={tier}>
                              {TICKET_TIER_LABELS[tier]}
                            </option>
                          ))}
                        </select>
                        {editTier === "inner_circle_plus_more" && (
                          <div className="inline-edit-field">
                            <label
                              htmlFor={`plus-one-${guest.id}`}
                              className="inline-edit-label"
                            >
                              Plus-One Allowance
                            </label>
                            <input
                              id={`plus-one-${guest.id}`}
                              type="number"
                              min={0}
                              max={10}
                              value={editPlusOnes}
                              onChange={(e) =>
                                setEditPlusOnes(parseInt(e.target.value || "0", 10))
                              }
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        {TICKET_TIER_LABELS[guest.ticket_tier || "party_not_presents"]}
                        {guest.ticket_tier === "inner_circle_plus_more" &&
                          guest.plus_one_limit != null &&
                          guest.plus_one_limit > 0 && (
                            <span className="tier-badge"> +{guest.plus_one_limit}</span>
                          )}
                      </>
                    )}
                  </td>
                  <td>
                    <span
                      className={`status-badge status-${guest.rsvp?.status || "pending"}`}
                    >
                      {rsvpStatusLabel(guest)}
                    </span>
                  </td>
                  <td>
                    <div className="request-actions">
                      {!guest.is_plus_one && editingId === guest.id ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={savingEdit}
                            onClick={() => saveEdit(guest.id)}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => setEditingId(null)}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          {!guest.is_plus_one && (
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => startEdit(guest)}
                            >
                              Edit
                            </button>
                          )}
                          {!guest.is_plus_one &&
                            guest.email &&
                            (!guest.rsvp?.status || guest.rsvp.status === "pending") && (
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                disabled={resendingId === guest.id}
                                onClick={() => handleResendInvite(guest.id)}
                              >
                                {resendingId === guest.id ? "Sending..." : "Resend invite"}
                              </button>
                            )}
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleRemove(guest.id)}
                          >
                            Remove
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <GuestCsvUpload
        weddingId={weddingId}
        existingGuests={guests}
        onSuccess={(emailWarning) => {
          if (emailWarning) setWarning(emailWarning);
          loadGuests();
        }}
      />

      <form className="form guest-add-form" onSubmit={handleAdd}>
        <h4>Add Guest</h4>
        <div className="form-group">
          <label htmlFor="guest_name">Full Name *</label>
          <input
            id="guest_name"
            value={form.full_name}
            onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="guest_email">Email (sends invitation)</label>
          <input
            id="guest_email"
            type="email"
            value={form.email}
            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
          />
          <p className="form-hint">
            Add an email to send an invitation with the couple&apos;s names, date, venue, and
            an RSVP link. Guests do not need an account.
          </p>
        </div>
        <div className="form-group">
          <label htmlFor="ticket_tier">Ticket Tier</label>
          <select
            id="ticket_tier"
            value={form.ticket_tier}
            onChange={(e) =>
              setForm((p) => ({
                ...p,
                ticket_tier: e.target.value as GuestTicketTier,
                plus_one_limit: 0,
              }))
            }
          >
            {GUEST_TIER_OPTIONS.map((tier) => (
              <option key={tier} value={tier}>
                {TICKET_TIER_LABELS[tier]}
              </option>
            ))}
          </select>
          <p className="form-hint">{TICKET_TIER_DESCRIPTIONS[form.ticket_tier]}</p>
        </div>
        {form.ticket_tier === "inner_circle_plus_more" && (
          <div className="form-group">
            <div className="label-with-tooltip">
              <label htmlFor="plus_one_limit">Plus-One Allowance</label>
              <span
                className="tooltip-trigger"
                tabIndex={0}
                aria-describedby="plus-one-tooltip"
              >
                ?
                <span className="tooltip-content" id="plus-one-tooltip" role="tooltip">
                Give your guests the ability to invite a plus-one. A plus-one allowance lets guests RSVP with up to the number of additional guests you allow.
                </span>
              </span>
            </div>
            <input
              id="plus_one_limit"
              type="number"
              min={0}
              max={10}
              value={form.plus_one_limit}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  plus_one_limit: parseInt(e.target.value || "0", 10),
                }))
              }
            />
          </div>
        )}
        <button type="submit" className="btn btn-primary" disabled={adding}>
          {adding ? "Adding..." : "Add Guest"}
        </button>
      </form>
    </div>
  );
};

export default GuestListManager;
